export type UserRole = "admin" | "manager" | "agent" | "demo";

export const canAccessAdminSurface = (role: UserRole, isDemo: boolean): boolean =>
  role === "admin" && !isDemo;

export const canAccessAdminContent = (
  role: UserRole,
  isDemo: boolean,
  featureEnabled = false
): boolean => canAccessAdminSurface(role, isDemo) && featureEnabled;

export interface CapabilityContext {
  role: UserRole;
  isDemo: boolean;
  isMobile: boolean;
  leadIsVerified?: boolean;
}

export interface Capabilities {
  canSeeNeedsAttention: boolean;
  canOpenInsightsFromProperties: boolean;
  canUseUnitMode: boolean;
  canSeeTeamActivity: boolean;
  canSeeLeadOpportunities: boolean;
  canSeeFreshnessIndicator: boolean;
  canRefreshData: boolean;
  canToggleEmailImages: boolean;
  canManageAssignments: boolean;
  hideMobileSummaryStats: boolean;
  canVerifyLead: boolean;
  canSendSms: boolean;
}

export function buildCapabilities(ctx: CapabilityContext): Capabilities {
  const { role, isDemo, isMobile, leadIsVerified } = ctx;
  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const isAgent = role === "agent";
  const elevated = isAdmin || isManager;
  const productionRole = isAdmin || isManager || isAgent;

  return {
    canSeeNeedsAttention: productionRole || isDemo,
    canOpenInsightsFromProperties: !isDemo && productionRole,
    canUseUnitMode: !isDemo && productionRole,
    // Team-wide activity is an operator surface. Agents should stay focused
    // on their assigned book of business and never receive aggregate teammate
    // data just because they can open the dashboard.
    canSeeTeamActivity: elevated,
    canSeeLeadOpportunities: !isDemo && elevated,
    canSeeFreshnessIndicator: productionRole,
    // Manager role processes leads + curates property contact info, so
    // they refresh data alongside admins (Grant 2026-04-25 elevation).
    canRefreshData: elevated,
    canToggleEmailImages: !isDemo && productionRole,
    canManageAssignments: elevated,
    hideMobileSummaryStats: isMobile && productionRole,
    // Verification spends real vendor dollars via /api/leads/{id}/verify.
    // Manager-or-admin gate (Grant 2026-04-25 — manager role processes leads
    // including verification + SMS sends + property mutations).
    canVerifyLead: !isDemo && elevated,
    // SMS send mirrors the backend gate in routes/sms.py: manager-or-admin
    // AND the lead must already have verification_provider set. Pass
    // leadIsVerified when calling per-lead; omit for a general "can
    // elevated user send SMS at all" check that won't be true until a lead
    // is selected and known-verified.
    canSendSms: !isDemo && elevated && leadIsVerified === true,
  };
}
